import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
} from "@material-ui/core";
import { toast } from "react-toastify";

import {
  getUserPersonalInfo,
  editProfileDataAPI,
} from "../../../../api/userApi";
import { useCustomContentStyles } from "./styles";

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}

export interface EarningsHistoryItem {
  amount: number;
  type: "subscription" | "ppv" | "custom" | "tip";
  date: Date;
  payoutStatus: boolean;
}

export interface UserPricingData {
  subscriptionPrice: SubscriptionTier[];
  ppvRate: number;
  customContentPrice: number; // Ціна для custom video
  tipMinimum: number;
  suggestedTipAmounts: number[];
  earningsHistory: EarningsHistoryItem[];
  customVideoDescription?: string; // Опис для custom video
  customVideoSubtitle?: string; // Підзаголовок (нове поле)
}

interface CustomVideoSettings {
  title: string; // Фіксований заголовок
  subtitle: string; // Фіксований підзаголовок
  price: number; // Редагована ціна
}

const CustomContentPricing: React.FC = () => {
  const classes = useCustomContentStyles();
  const [settings, setSettings] = useState<CustomVideoSettings>({
    title: "Request a Custom Video", // Фіксований заголовок
    subtitle:
      "Want a personalized video from your favorite creator? Submit your request now!", // Фіксований підзаголовок
    price: 100, // Дефолтна ціна з дизайну
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getUserPersonalInfo();
        const userData = res.data as UserPricingData;
        setSettings({
          title: "Request a Custom Video", // Фіксований
          subtitle:
            userData.customVideoSubtitle ||
            "Want a personalized video from your favorite creator? Submit your request now!", // Фіксований
          price: userData.customContentPrice || 100,
        });
      } catch (error) {
        toast.error("Failed to load custom video settings");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const updateSetting = (field: keyof CustomVideoSettings, value: any) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("customContentPrice", settings.price.toString());

    try {
      await editProfileDataAPI(formData);
      toast.success("Custom video settings updated successfully");
    } catch (error) {
      toast.error("Failed to update custom video settings");
      console.error(error);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Custom Content Pricing
      </Typography>
      <Typography className={classes.infoText}>
        Set pricing and details for custom video requests.
      </Typography>
      <Box
        style={{
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "4px",
          padding: "12px",
          marginBottom: "24px",
        }}
      >
        <Typography
          style={{
            color: "#856404",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          ⚠️ Important: All custom videos must be delivered within 7 business
          days of order confirmation.
        </Typography>
      </Box>

      <Card className={classes.serviceCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.formRow}>
            <Typography className={classes.label}>Title</Typography>
            <TextField
              className={classes.inputField}
              variant="outlined"
              size="small"
              value={settings.title}
              disabled // Фіксований заголовок
              placeholder="Request a Custom Video"
            />
          </Box>

          <Box className={classes.formRow}>
            <Typography className={classes.label}>Subtitle</Typography>
            <Box className={classes.fieldContainer}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={settings.subtitle}
                disabled // Фіксований підзаголовок
                multiline
                className={classes.inputField}
                inputProps={{ maxLength: 150 }}
              />
              <Typography className={classes.bioCounter}>
                {settings.subtitle.length} / 150
              </Typography>
            </Box>
          </Box>

          <Box className={classes.formRow}>
            <Typography className={classes.label}>Price ($)</Typography>
            <TextField
              className={classes.inputField}
              variant="outlined"
              size="small"
              type="number"
              value={settings.price}
              onChange={e => updateSetting("price", Number(e.target.value))}
              placeholder="100.00"
            />
          </Box>
        </CardContent>
      </Card>

      <Button className={classes.saveButton} onClick={handleSave}>
        Save Changes
      </Button>
    </div>
  );
};

export default CustomContentPricing;
